import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import SeoFields, { inputCls, labelCls, emptySeo } from "./SeoFields";
import { Loader2, Save } from "lucide-react";

const SettingsManager = () => {
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS({ ...data, defaultSeo: data.defaultSeo || { ...emptySeo } }))
      .catch(() => toast({ title: "Failed to load settings", variant: "destructive" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast({ title: "Settings saved", description: "SEO & analytics scripts updated." });
    } catch (e) { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00ff87]" size={32} /></div>;

  return (
    <div className="space-y-5 max-w-3xl">
      <h2 className="font-display text-xl font-bold text-white">Site & SEO Settings</h2>

      <div>
        <label className={labelCls}>Site Name</label>
        <input className={inputCls} value={s.siteName || ""} onChange={(e) => setS({ ...s, siteName: e.target.value })} />
      </div>

      <div className="rounded-xl border border-[#00e5ff]/15 bg-[#0a0a13] p-4 space-y-4">
        <h4 className="font-display font-semibold text-[#00e5ff] text-sm">Analytics & Search Console (SEO Plugins)</h4>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Google Analytics ID (GA4)</label>
            <input className={inputCls} value={s.gaId || ""} onChange={(e) => setS({ ...s, gaId: e.target.value })} placeholder="G-XXXXXXXXXX" />
          </div>
          <div>
            <label className={labelCls}>Google Tag Manager ID</label>
            <input className={inputCls} value={s.gtmId || ""} onChange={(e) => setS({ ...s, gtmId: e.target.value })} placeholder="GTM-XXXXXXX" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Search Console Verification Code</label>
          <input className={inputCls} value={s.searchConsoleTag || ""} onChange={(e) => setS({ ...s, searchConsoleTag: e.target.value })} placeholder="google-site-verification content value" />
        </div>
        <div>
          <label className={labelCls}>Custom Head Scripts / Meta (raw HTML)</label>
          <textarea rows={4} className={inputCls} value={s.headScripts || ""} onChange={(e) => setS({ ...s, headScripts: e.target.value })} placeholder="<script>...</script> or <meta ...>" />
        </div>
      </div>

      <SeoFields seo={s.defaultSeo} onChange={(defaultSeo) => setS({ ...s, defaultSeo })} />

      <button onClick={save} disabled={saving} className="btn-neon inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold">
        {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save Settings
      </button>
    </div>
  );
};

export default SettingsManager;
